import { 
  authenticatedGet, 
  authenticatedPost, 
  authenticatedPut 
} from '../../api/authenticatedFetch';

const BASE_API = '/api/v1';

// --- Budgets ---

export const listBudgets = async ({ status, q }) => {
  const params = {};
  if (status) params.status = status;
  if (q) params.q = q;
  return authenticatedGet(`${BASE_API}/budgets`, params);
};

export const createBudget = async (payload) => {
  console.log("[createBudget] POST /api/v1/budgets, payload:", payload);
  try {
    const res = await authenticatedPost(`${BASE_API}/budgets`, payload);
    console.log("[createBudget] response:", res);
    return res;
  } catch (err) {
    console.error("[createBudget] request failed:", err);
    console.error("[createBudget] err.message:", err?.message);
    console.error("[createBudget] err.status:", err?.status);
    console.error("[createBudget] err.serverBody:", err?.serverBody);
    throw err;
  }
};

export const getBudget = async (id) => {
  return authenticatedGet(`${BASE_API}/budgets/${id}`);
};

export const updateBudget = async (id, payload) => {
  return authenticatedPut(`${BASE_API}/budgets/${id}`, payload);
};

export const archiveBudget = async (id, payload = {}) => {
  // archive is a POST action
  return authenticatedPost(`${BASE_API}/budgets/${id}/archive`, payload);
};

export const submitBudgetActivation = async (id) => {
  return authenticatedPost(`${BASE_API}/budgets/${id}/submit-activation`, {});
};

export const pauseBudget = async (id) => {
  return authenticatedPost(`${BASE_API}/budgets/${id}/pause`, {});
};

export const resumeBudget = async (id) => {
  return authenticatedPost(`${BASE_API}/budgets/${id}/resume`, {});
};

// --- Risk (cockpit risk cards) ---

// Envelope is `{ risk: {...} }` (writeJSON wraps under "risk" — see
// reference_writejson-envelope-wrapping); unwrap so callers get the risk
// payload directly.
export const getBudgetRisk = async (id) => {
  const res = await authenticatedGet(`${BASE_API}/budgets/${id}/risk`);
  return res?.risk ?? null;
};

// --- Owners ---

export const getBudgetOwners = async (id) => {
  return authenticatedGet(`${BASE_API}/budgets/${id}/owners`);
};

export const updateBudgetOwners = async (id, payload) => {
  return authenticatedPut(`${BASE_API}/budgets/${id}/owners`, payload);
};

// --- Scope ---

const EMPTY_SCOPE = { scope_mode: "include", clauses: [] };

function normalizeScope(raw) {
  const scope = raw?.scope ?? raw;
  return {
    scope_mode: scope?.scope_mode ?? "include",
    clauses: Array.isArray(scope?.clauses) ? scope.clauses : [],
  };
}

export const getBudgetScope = async (id) => {
  try {
    const res = await authenticatedGet(`${BASE_API}/budgets/${id}/scope`);
    return normalizeScope(res);
  } catch (err) {
    if (err?.status === 404) {
      return EMPTY_SCOPE;
    }
    throw err;
  }
};

export const updateBudgetScope = async (id, payload) => {
  return authenticatedPut(`${BASE_API}/budgets/${id}/scope`, payload);
};

// --- Periods (Phase 3) ---

export const listBudgetPeriods = async (budgetId) => {
  return authenticatedGet(`${BASE_API}/budgets/${budgetId}/periods`);
};

export const generateBudgetPeriods = async (budgetId, { horizon } = {}) => {
  const params = horizon ? `?horizon=${horizon}` : '';
  return authenticatedPost(`${BASE_API}/budgets/${budgetId}/periods/generate${params}`, {});
};

export const getPeriodVersions = async (periodId) => {
  return authenticatedGet(`${BASE_API}/budget-periods/${periodId}/versions`);
};

export const createDraftPeriodVersion = async (periodId) => {
  return authenticatedPost(`${BASE_API}/budget-periods/${periodId}/versions`, {});
};

export const submitPeriodVersion = async (versionId, payload) => {
  console.log('[submitPeriodVersion] request', { versionId, payload });
  const result = await authenticatedPost(`${BASE_API}/budget-period-versions/${versionId}/submit`, payload);
  console.log('[submitPeriodVersion] response', { versionId, result, versionStatus: result?.version?.status });
  return result;
};

export const activatePeriodVersion = async (versionId) => {
  return authenticatedPost(`${BASE_API}/budget-period-versions/${versionId}/activate`, {});
};

// --- Audit ---

export const listAuditEvents = async ({ objectType, objectId, limit }) => {
  const params = {};
  if (objectType) params.object_type = objectType;
  if (objectId) params.object_id = objectId;
  if (limit) params.limit = limit;
  return authenticatedGet(`${BASE_API}/audit-events`, params);
};

// --- Hierarchy / Dimensions ---

export const listBudgetDimensions = async () => {
  return authenticatedGet(`${BASE_API}/budget-dimensions`);
};

export const getActiveModelVersion = async (dimensionId) => {
  const params = { dimension_id: dimensionId };
  return authenticatedGet(`${BASE_API}/cost-model-versions/active`, params);
};

export const searchNodes = async ({ modelVersionId, q, limit }) => {
  const params = {};
  if (q != null && q !== '') params.q = q;
  if (limit != null) params.limit = limit;
  return authenticatedGet(`${BASE_API}/cost-models/${modelVersionId}/nodes`, params);
};

// Latest evaluated projection per budget, for the list's forecast column. Separate from
// listBudgets because it comes from the evaluation job's rows rather than the budget
// records themselves; the two are joined by budget id in the page.
export const getBudgetForecastTotals = async () => {
  return authenticatedGet(`${BASE_API}/budgets/forecast-monthly`);
};
